import React from 'react';
import { Link } from 'react-router-dom';
import { Alert, Box, Button, IconButton, Paper, Skeleton, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DailyBriefingCard from './DashboardContents/Assistant/DailyBriefingCard';
import useFetch from '../../hooks/useFetch';
import { API } from '../../utils/apiRoutes';
import { toPersianDigits } from '../../utils/convertNumbers';
import { formatJalaliDateTime } from '../../utils/formatJalaliDate';
import { useAuth } from '../../context/AuthContext';
import { canAccessSection } from '../../config/sectionAccess';

// The dashboard home («نمای کلی»). Institute details (name, date, user) live
// in the header only. For manager/secretary/accountant it shows role-scoped,
// real numbers from /assistant/overview/ (never placeholder zeros), the items
// that need follow-up, and — for the manager — the AI daily summary, which
// is optional: the numbers never depend on it.

// Attention keys come from the server; which page they open is decided here.
const ATTENTION_ROUTES = {
    unrecorded_today: { section: 'student-card', page: 'حضور و غیاب' },
    debtors: { section: 'student-accounting', page: 'حسابداری هنرجویان' },
    unresolved_cashbox: { section: 'cashbox-reconciliation', page: 'تطبیق صندوق' },
    unmatched_bank: { section: 'bank-reconciliation', page: 'تطبیق بانکی' },
    delayed_enrollments: { section: 'student-list', page: 'فهرست هنرجویان' },
};

const formatNumber = (value) =>
    typeof value === 'number' ? toPersianDigits(value.toLocaleString('en-US')) : toPersianDigits(value ?? '—');

const cardSx = (theme) => ({
    p: { xs: 2, sm: 2.5 },
    borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.border.main, 0.35)}`,
    backgroundImage: 'none',
});

function MetricTile({ metric }) {
    const theme = useTheme();
    return (
        <Paper elevation={0} sx={{ ...cardSx(theme), display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {metric.label}
            </Typography>
            <Typography sx={{ fontSize: { xs: '1.15rem', sm: '1.45rem' }, fontWeight: 700, lineHeight: 1.3, overflowWrap: 'anywhere' }}>
                {formatNumber(metric.value)}
                {metric.unit && (
                    <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 400, ml: 0.5, mr: 0.5, display: 'inline-block', whiteSpace: 'nowrap' }}>
                        {metric.unit}
                    </Typography>
                )}
            </Typography>
            {metric.period && (
                <Typography variant="caption" color="text.secondary">
                    {metric.period}
                </Typography>
            )}
        </Paper>
    );
}

function AttentionCard({ items, user }) {
    const theme = useTheme();
    return (
        <Paper elevation={0} sx={cardSx(theme)}>
            <Typography component="h2" sx={{ fontWeight: 700, fontSize: '1rem', mb: 1.5 }}>
                نیازمند رسیدگی
            </Typography>
            {items.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <CheckCircleOutlineIcon fontSize="small" color="success" />
                    موردی برای پیگیری نیست.
                </Typography>
            ) : (
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                    {items.map((item) => {
                        const route = ATTENTION_ROUTES[item.key];
                        const linkable = route && canAccessSection(user, route.section);
                        return (
                            <Box
                                component="li"
                                key={item.key}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    py: 1,
                                    flexWrap: 'wrap',
                                    '&:not(:last-of-type)': { borderBottom: `1px solid ${alpha(theme.palette.border.main, 0.25)}` },
                                }}
                            >
                                <PriorityHighIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />
                                <Typography variant="body2" sx={{ flex: 1, minWidth: 140 }}>
                                    <Box component="strong" sx={{ ml: 0.5 }}>{formatNumber(item.count)}</Box>
                                    {' '}
                                    {item.label}
                                </Typography>
                                {linkable && (
                                    <Button size="small" component={Link} to={`/dashboard/${route.section}`} sx={{ flexShrink: 0 }}>
                                        {`مشاهده در «${route.page}»`}
                                    </Button>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Paper>
    );
}

function DashboardWelcome() {
    const theme = useTheme();
    const { user } = useAuth();
    const hasOverview = canAccessSection(user, 'assistant');
    const { data, loading, error, refetch } = useFetch(hasOverview ? API.assistantOverview : null, {
        skip: !hasOverview,
    });

    return (
        <Box sx={{ maxWidth: 1400, mx: 'auto', '& .MuiTypography-root': { textAlign: 'start' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Typography component="h1" sx={{ m: 0, fontSize: '1.15rem', fontWeight: 700, color: theme.palette.text.default }}>
                    نمای کلی
                </Typography>
                {data && (
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                        {toPersianDigits(`داده‌های ${data.date_jalali} · به‌روزرسانی ${formatJalaliDateTime(data.generated_at)}`)}
                    </Typography>
                )}
                {hasOverview && (
                    <Tooltip title="به‌روزرسانی عددها">
                        <span>
                            <IconButton size="small" onClick={() => refetch().catch(() => {})} disabled={loading} aria-label="به‌روزرسانی عددهای نمای کلی">
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Box>

            {!hasOverview && (
                <Typography color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                    بخش مورد نظر را از منو انتخاب کنید.
                </Typography>
            )}

            {hasOverview && error && !data && (
                <Alert
                    severity="error"
                    action={<Button color="inherit" size="small" onClick={() => refetch().catch(() => {})}>تلاش دوباره</Button>}
                >
                    {`عددهای نمای کلی دریافت نشد: ${error}`}
                </Alert>
            )}

            {hasOverview && loading && !data && (
                <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
                    {[0, 1, 2, 3].map((i) => (
                        <Skeleton key={i} variant="rounded" height={104} />
                    ))}
                </Box>
            )}

            {data && (
                <>
                    <Box
                        component="section"
                        aria-label="شاخص‌ها"
                        sx={{
                            display: 'grid',
                            gap: 1.5,
                            gridTemplateColumns: {
                                xs: 'repeat(2, minmax(0, 1fr))',
                                md: `repeat(${Math.min(Math.max(data.metrics?.length || 1, 1), 4)}, minmax(0, 1fr))`,
                            },
                            mb: 2,
                        }}
                    >
                        {(data.metrics || []).map((metric) => (
                            <MetricTile key={metric.key} metric={metric} />
                        ))}
                    </Box>
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 1.5,
                            alignItems: 'start',
                            gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: data.role === 'manager' ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)' },
                        }}
                    >
                        <AttentionCard items={data.attention || []} user={user} />
                        {/* Manager-only endpoint: requested only for the role the server reported. */}
                        {data.role === 'manager' && <DailyBriefingCard />}
                    </Box>
                </>
            )}
        </Box>
    );
}

export default DashboardWelcome;
